import React from 'react'
import {
  SidebarProvider, Sidebar, SidebarHeader, SidebarContent,
  SidebarFooter, SidebarMenu, SidebarMenuItem, SidebarMenuButton,
  SidebarTrigger, SidebarGroup, SidebarGroupLabel, SidebarRail,
  SidebarInset, useSidebar,
} from '@/components/ui/sidebar'
import { Toaster } from '@/components/ui/sonner'
import { useFlash } from '@/hooks/use-flash'
import { Link, usePage } from '@inertiajs/react'
import {
  LayoutDashboard, Users, UserPlus, BarChart2, Network, Shield,
  HandCoins, Target, Gift, RefreshCw, Megaphone, CreditCard, CheckSquare, Bell,
  Upload, CalendarDays, Tags,
} from 'lucide-react'
import { Logo } from '@/components/Logo'
import { SidebarUserMenu } from '@/components/SidebarUserMenu'

type NavItem = {
  label: string
  href: string
  icon: React.ComponentType<{ className?: string }>
}

// Navigation sections, separated by SidebarGroups to render as visual dividers:
// 1. Overview:   Dashboard, Reporting
// 2. People:     Prospects, Donnateurs, Campagnes, Opportunités
// 3. Donations:  Dons, Dons récurrents
// 4. Team:       Mon équipe, Tâches
const overviewItems: NavItem[] = [
  { label: 'Dashboard', href: '/',          icon: LayoutDashboard },
  { label: 'Reporting', href: '/reporting', icon: BarChart2 },
]

const peopleItems: NavItem[] = [
  { label: 'Prospects',    href: '/people/prospects',          icon: UserPlus },
  { label: 'Donnateurs',   href: '/people/donors',             icon: Users },
  { label: 'Campagnes',    href: '/fundraising/campaigns',     icon: Megaphone },
  { label: 'Opportunités', href: '/fundraising/opportunities', icon: Target },
]

const donationItems: NavItem[] = [
  { label: 'Dons',            href: '/fundraising/donations',           icon: Gift },
  { label: 'Dons récurrents', href: '/fundraising/recurring_donations', icon: RefreshCw },
]

const teamItems: NavItem[] = [
  { label: 'Mon équipe', href: '/work/team-dashboard', icon: Network },
  { label: 'Tâches',     href: '/work/tasks',          icon: CheckSquare },
  { label: 'Calendrier', href: '/work/calendar',       icon: CalendarDays },
]

function NavMenu({ items }: { items: NavItem[] }) {
  const { url } = usePage()

  return (
    <SidebarMenu>
      {items.map((item) => {
        const isActive = item.href === '/'
          ? url === '/'
          : url.startsWith(item.href)

        return (
          <SidebarMenuItem key={item.href}>
            <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
              <Link href={item.href}>
                <item.icon className="size-4" />
                <span>{item.label}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )
      })}
    </SidebarMenu>
  )
}

function SidebarLogoHeader() {
  const { state } = useSidebar()

  return (
    <SidebarHeader>
      <div className="flex items-center gap-2 px-2 py-1">
        <Logo variant={state === 'collapsed' ? 'icon' : 'full'} />
      </div>
    </SidebarHeader>
  )
}

const adminItems: NavItem[] = [
  { label: 'Imports',            href: '/people/imports/organization_donors', icon: Upload },
  { label: 'Moyens de paiement', href: '/fundraising/payment_methods',        icon: CreditCard },
  { label: 'Natures de don',     href: '/fundraising/donation_natures',       icon: Tags },
  { label: 'Teams',              href: '/admin/teams',                        icon: Network },
  { label: 'Users',              href: '/admin/users',                        icon: Shield },
]

function AdminSection() {
  const { auth } = usePage().props
  if (auth.user?.role !== 'space_admin') return null

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Admin</SidebarGroupLabel>
      <NavMenu items={adminItems} />
    </SidebarGroup>
  )
}

// Top-bar Notifications link — Sprint 5a / Unit 14c (PRODUCT-002 cut).
// Reads `unread_notifications_count` from shared Inertia props (set by
// the host ApplicationController). PRODUCT-002 cut: no JS polling, no
// dropdown — the count refreshes naturally on every page navigation.
function NotificationsTopbarLink() {
  // unread_notifications_count is a shared prop set in the host
  // ApplicationController. It's always a number for authenticated
  // users; 0 when the user has no unread items.
  const props = usePage().props as { unread_notifications_count?: number }
  const count = props.unread_notifications_count ?? 0

  return (
    <Link
      href="/work/notifications"
      className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-accent"
    >
      <Bell className="h-4 w-4" />
      <span>Notifications</span>
      {count > 0 && (
        <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
          {count}
        </span>
      )}
    </Link>
  )
}

interface AppLayoutProps {
  children: React.ReactNode
}

export function AppLayout({ children }: AppLayoutProps) {
  useFlash()

  return (
    <>
      <SidebarProvider>
        <Sidebar variant="inset" collapsible="icon">
          <SidebarLogoHeader />
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>Vue d'ensemble</SidebarGroupLabel>
              <NavMenu items={overviewItems} />
            </SidebarGroup>
            <SidebarGroup>
              <SidebarGroupLabel>Contacts & Campagnes</SidebarGroupLabel>
              <NavMenu items={peopleItems} />
            </SidebarGroup>
            <SidebarGroup>
              <SidebarGroupLabel>Collecte de fonds</SidebarGroupLabel>
              <NavMenu items={donationItems} />
            </SidebarGroup>
            <SidebarGroup>
              <SidebarGroupLabel>Équipe & Tâches</SidebarGroupLabel>
              <NavMenu items={teamItems} />
            </SidebarGroup>
            <AdminSection />
          </SidebarContent>
          <SidebarFooter>
            <SidebarUserMenu />
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>
        <SidebarInset>
          <header className="flex h-12 items-center justify-between px-4">
            <SidebarTrigger />
            <NotificationsTopbarLink />
          </header>
          <div className="flex-1 overflow-auto p-6">
            {children}
          </div>
        </SidebarInset>
      </SidebarProvider>
      <Toaster position="bottom-right" richColors />
    </>
  )
}
